import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setNewPassChange } from '../store/User.action';
import Loader from '../../../Components/Utility/Loader/Loader';
import { BombiixModal } from '../../../Components/BombiixModal';
import { NewInput } from '../../../Components/PIM/common/NewInput';

/**
 * Set-new-password popup in the master-design modal chrome: blue Submit on the left,
 * centered title, red close on the right, two `NewInput` password fields in the body.
 * @param {object} props - Component props.
 * @param {boolean} props.show - Whether the popup is open.
 * @param {string} props.title - Popup title.
 * @param {Function} props.toggle - Closes the popup.
 * @param {object} props.user - User whose password is being set; `_id` is sent to the API.
 * @returns {JSX.Element} The set-password popup.
 */
function SetNewPassword(props) {
  const { show, title, toggle, user } = props;
  const [showLoader, setShowLoader] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const validatePassRegex = (val) => {
    const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^a-zA-Z\d]).{8,}$/;
    return regex.test(val);
  };

  const errorMsg = () => {
    return t('SET_NEW_PASSWORD.PASSWORD_MESSAGES');
  };
  const validate = {
    validateCnfPass: (val) => {
      if (!validatePassRegex(val)) {
        return errorMsg();
      }
      if (!val?.trim()) {
        return t('SET_NEW_PASSWORD.CONFIRM_PASSWORD_REQUIRED');
      }
      if (val !== watch('newPassword')) return t('SET_NEW_PASSWORD.PASSWORD_MISMATCH');
      return null;
    },
    validateNewPass: (val) => {
      if (!validatePassRegex(val)) {
        return errorMsg();
      }
      if (!val?.trim()) {
        return t('SET_NEW_PASSWORD.NEW_PASSWORD_REQUIRED');
      }
      return null;
    },
  };
  const onSubmit = async (val) => {
    const reqObj = { ...val, id: user._id };
    setShowLoader(true);
    const res = await dispatch(setNewPassChange(reqObj, t));
    if (res) toggle();
    setShowLoader(false);
  };

  return (
    <BombiixModal
      size="lg"
      show={show}
      title={title}
      onClose={toggle}
      onSave={handleSubmit(onSubmit)}
      saveLabel={t('SET_NEW_PASSWORD.SUBMIT')}
      saveLoading={showLoader}
    >
      {showLoader ? (
        <Loader />
      ) : (
        <form className="row" noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className="col-md-6 col-sm-6">
            <NewInput
              type="password"
              label={t('SET_NEW_PASSWORD.NEW_PASSWORD')}
              required
              invalid={Boolean(errors?.newPassword)}
              {...register('newPassword', { required: t('SET_NEW_PASSWORD.NEW_PASSWORD_REQUIRED'), validate: validate.validateNewPass })}
            />
            {errors?.newPassword?.message && <span className="error-message">{errors.newPassword.message}</span>}
          </div>
          <div className="col-md-6 col-sm-6">
            <NewInput
              type="password"
              label={t('SET_NEW_PASSWORD.CONFIRM_NEW_PASSWORD')}
              required
              invalid={Boolean(errors?.confirmPassword)}
              {...register('confirmPassword', { required: t('SET_NEW_PASSWORD.CONFIRM_PASSWORD_REQUIRED'), validate: validate.validateCnfPass })}
            />
            {errors?.confirmPassword?.message && <span className="error-message">{errors.confirmPassword.message}</span>}
          </div>
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      )}
    </BombiixModal>
  );
}

export default SetNewPassword;
